import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { AIMateAnalyticsContext } from '@atlaskit/analytics-namespaced-context/AIMateAnalyticsContext';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import { FlagsProvider } from '@atlaskit/flag/flags-provider';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import {
	ExperienceName,
	REMIX_GENERATE_PREVIEW_MARKS,
	remixGeneratePreviewExperience,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	abortRemixCreateExperience,
	succeedRemixCreateExperienceAtHandoff,
	succeedRemixCreateExperienceForSafetyRefusal,
} from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { getRemixFeatureArea } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-feature-area';
import { REMIX_IMAGE_GENERATION_SAFETY_REFUSAL_OUTCOME } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-outcomes';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { useStagingAreaState } from '@atlassian/conversation-assistant-store/controllers/staging-area';

import { RemixContainer } from '../remix';
import type { RemixOptionSelectionParameters } from '../remix/remix-overlay';
import { EditorRemixContextProvider, useEditorRemixContext } from './context';
import { deriveLegacyEditorRemixPhase } from './editorRemixLifecycle';
import {
	admitAndRetainEditorRemixLifecycle,
	type RetainedEditorRemixLifecycle,
	useEditorRemixLifecycle,
	useEditorRemixLifecycleOwner,
} from './editorRemixLifecycleOwner';
import { LocationPickerPopup } from './LocationPickerPopup';
import {
	DEFAULT_REMIX_ERROR_MESSAGE,
	REMIX_REMOTE_ABORT_REASON as REMOTE_ABORT_REASON,
} from './types';
import type {
	DynamicUiSource,
	EditorApiWithCorePlugin,
	EditorRemixExperienceSettlement,
	EditorRemixLifecyclePhase,
	EditorRemixUxHandles,
	ExternalPreviewInsertionNotAcknowledged,
	HandoffAcknowledgementResult,
	OpenRemixPreviewOptions,
	RemixContentComponentFullProps,
	RemixHistoryEntryForUpdate,
} from './types';
import { useOnComplete as useOnCompleteNew } from './useOnComplete';
import { useUpdateRemix, type UpdateRemix } from './useUpdateRemix';
import { prepareRemixFragmentNodes, type RemixActionReadyOptions, useRemixToAIFC } from './utils';

function OnCompleteSync(): null {
	useOnCompleteNew();
	return null;
}

/**
 * Bounds the wait for a preview or staging surface to acknowledge opening (`onOpen`). Distinct from
 * the insertion acknowledgement timeout: expiry only releases shadow ownership as `abandoned`.
 */
const PREVIEW_HANDOFF_ACKNOWLEDGEMENT_TIMEOUT_MS = 10_000;

/** The experience-tracker reason for a locally cancelled flow. */
const CANCELLATION_ABORT_REASON = 'User cancelled remix thinking';

type HandoffSurface = 'preview' | 'rovo-chat' | 'staging';

type UnacknowledgedHandoff = Exclude<HandoffAcknowledgementResult, { outcome: 'acknowledged' }>;

/**
 * Exactly one outcome wins: the acknowledgement or the timeout's `abandoned` settlement. The timer
 * deliberately outlives the component that armed it (legacy staging cleanup unmounts it
 * synchronously), so both paths act only on the token-bound handle. An unacknowledged handoff is
 * not a cancellation: no machine terminal event is sent. `onTimedOut` is diagnostics only and runs
 * only when the timeout actually released ownership; a flow already settled by another terminal
 * path is not reported as a handoff abandonment.
 */
const armPreviewAcknowledgement = (
	lifecycle: RetainedEditorRemixLifecycle,
	onAcknowledged: () => void,
	onTimedOut: () => void,
): (() => void) => {
	let settled = false;

	const timer = setTimeout(() => {
		if (settled) {
			return;
		}
		settled = true;
		if (lifecycle.end('abandoned')) {
			onTimedOut();
		}
	}, PREVIEW_HANDOFF_ACKNOWLEDGEMENT_TIMEOUT_MS);

	return () => {
		if (settled) {
			return;
		}
		settled = true;
		clearTimeout(timer);
		onAcknowledged();
	};
};

type RemixUpdateSubmissionHistoryEntry = Pick<
	RemixHistoryEntryForUpdate,
	'conversationId' | 'fieldId' | 'url'
>;

export const getUpdateRemixSubmissionDecision = (
	historyEntryForUpdate: RemixUpdateSubmissionHistoryEntry | undefined,
	dynamicUiSubtypeForUpdate: string | undefined,
	submittedUpdateKey: string | undefined,
): { shouldSubmit: boolean; updateKey?: string } => {
	if (!historyEntryForUpdate || !dynamicUiSubtypeForUpdate) {
		return { shouldSubmit: false };
	}

	const updateKey = [
		historyEntryForUpdate.conversationId,
		historyEntryForUpdate.url ?? historyEntryForUpdate.fieldId ?? '',
		dynamicUiSubtypeForUpdate,
	].join(':');

	return {
		shouldSubmit: submittedUpdateKey !== updateKey,
		updateKey,
	};
};

/**
 * editor has two modes, editing / viewing
 * - for editing mode, the thinking bar is rendered by the editor plugin
 * - for viewing mode, the thinking bar is rendered by the malleable-ui components
 * so the flow is different in two modes, specifically the onCompleteAnimationFinished callback
 */
const RemixContentComponentInner = <
	TEditorApi extends EditorApiWithCorePlugin = EditorApiWithCorePlugin,
>({
	handleModalClose,
	selectedContext,
	editorApi,
	isComplete: pluginIsComplete,
	isThinking,
	isEditorRemixStateRearchitectureTreatment,
	remixAttemptId,
	onStartThinking,
	onDismissThinkingUi,
	onStopThinking,
	onRegisterAbortRequest,
	onRegisterEditorRemixUxHandles,
	onRegisterEditorRemixMutationCancellationSink,
	onBeforeEditorRemixResultInsertion,
	finishRemixThinking,
	remixModalMetadata,
	remixSource,
	productIntegration,
	productConfig,
	hiddenOptions,
	isUiVisible = true,
}: RemixContentComponentFullProps<TEditorApi> & {
	/**
	 * Whether this subtree should render anything. Mounting and rendering are separate concerns
	 * here: the hooks below own the conversation subscription that produces `finalMessage`, and
	 * that has to keep running for the whole flow, including the window where no remix UI belongs
	 * on screen. Unmounting instead of rendering `null` drops the subscription, and the final
	 * message then lands with nothing listening.
	 */
	isUiVisible?: boolean;
}) => {
	const { getRemixGeneratePreviewMetadata } = productConfig;
	const lifecycleOwner = useEditorRemixLifecycleOwner();
	const retainedLifecycle = lifecycleOwner?.active;
	/**
	 * Whether a machine session owns this flow's terminal effects.
	 *
	 * Owned by `EditorRemixLifecycleOwnerProvider`, which latches it inside `retain` and clears it per
	 * flow start, rather than being re-derived here: it has to outlive both the machine ownership the
	 * observer releases before dispatching terminal commands *and* the read session that the terminal
	 * command's own cleanup dismisses.
	 */
	const { hasMachineAuthority: isMachineAuthoritative } = useEditorRemixLifecycle();
	const { closeConversationAssistant } = productIntegration;
	const {
		editorRemixLifecycleAdapter,
		isViewMode,
		setDynamicUiType,
		setDynamicUiSubtype,
		setDynamicUiSource,
		setInteractionSource,
		setAspectRatio,
		showLocationPicker,
		setThinkingComplete,
		clearThinkingDecoration,
		isUpdate,
		historyEntryForUpdate,
		dynamicUiSubtypeForUpdate,
		isRemixInsertingRef,
		onRemixThinkingCompleteAnimationFinished,
		resetContext,
	} = useEditorRemixContext();
	const isRemixCancelled = useRef(false);
	const hasCommittedRemixCreateTerminal = useRef(false);
	const experienceTracker = useAIMateExperienceTracker();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	// Records why a bounded handoff released the shadow lifecycle. Diagnostics only: it must never
	// throw into the lifecycle path, and the payload carries no identifiers.
	const logHandoffOutcomeBestEffort = useCallback(
		(surface: HandoffSurface, result: UnacknowledgedHandoff): void => {
			try {
				createAnalyticsEvent({
					eventType: 'operational',
					action: 'failed',
					actionSubject: 'editorRemixHandoff',
					actionSubjectId: surface,
					source: 'editorRemix',
					attributes: {
						operation: 'handoffAcknowledgement',
						surface,
						outcome: result.outcome,
						...(result.outcome === 'abandoned' ? { reason: result.reason } : {}),
					},
				}).fire();
			} catch {
				// Telemetry must never affect lifecycle behavior.
			}
		},
		[createAnalyticsEvent],
	);
	// Records a classified non-acknowledgement of an external preview's editor insertion. Diagnostics
	// only: no machine event, no ownership change, and it must never throw into the lifecycle path.
	const logExternalPreviewInsertionNotAcknowledgedBestEffort = useCallback(
		(result: ExternalPreviewInsertionNotAcknowledged): void => {
			try {
				createAnalyticsEvent({
					eventType: 'operational',
					action: 'failed',
					actionSubject: 'editorInsertionAcknowledgement',
					actionSubjectId: 'externalPreview',
					source: 'editorRemix',
					attributes: {
						operation: 'externalPreviewInsertion',
						insertionKind: result.insertionKind,
						outcome: result.outcome,
					},
				}).fire();
			} catch {
				// Telemetry must never affect lifecycle behavior.
			}
		},
		[createAnalyticsEvent],
	);
	const openRemixPreviewRef = useRef<
		(options?: Omit<OpenRemixPreviewOptions, 'isFloating' | 'remixConversationId'>) => void
	>(() => {});
	const selectedSubTypeRef = useRef<string | undefined>(undefined);
	const conversationIdRef = useRef<string | undefined>(undefined);
	const shouldOpenRovoChatOnCompleteRef = useRef(false);
	const hasAutoSubmittedRef = useRef(false);
	const remixModalMetadataRef = useRef(remixModalMetadata);
	remixModalMetadataRef.current = remixModalMetadata;
	const isThinkingRef = useRef(isThinking);
	isThinkingRef.current = isThinking;
	const isUpdateRef = useRef(isUpdate);
	isUpdateRef.current = isUpdate;
	const retainedLifecycleRef = useRef(retainedLifecycle);
	retainedLifecycleRef.current = retainedLifecycle;
	const isMachineAuthoritativeRef = useRef(isMachineAuthoritative);
	isMachineAuthoritativeRef.current = isMachineAuthoritative;
	const editorRemixLifecycleAdapterRef = useRef(editorRemixLifecycleAdapter);
	editorRemixLifecycleAdapterRef.current = editorRemixLifecycleAdapter;
	const updateRemixRef = useRef<UpdateRemix | undefined>(undefined);

	const { dynamicUiType } = useStagingAreaState();
	const featureArea = getRemixFeatureArea({ remixType: dynamicUiType });
	const remixStartAttributes = useMemo(
		() => ({ ...(featureArea !== undefined && { featureArea }) }),
		[featureArea],
	);
	const remixStartAttributesRef = useRef(remixStartAttributes);
	remixStartAttributesRef.current = remixStartAttributes;

	/**
	 * This flow's own cancellation intent, recorded before `CANCELLED` is sent to the machine.
	 *
	 * `RemixAborted` is origin-blind by design: `CANCELLED` transitions the machine to `aborted`, so a
	 * local cancel and a Rovo-side abort arrive as the same command. Recording the intent here is the
	 * documented way for a caller to tell them apart inside the abort handler.
	 *
	 * Scoped to the flow it was recorded for, and dropped when the machine refuses the transition.
	 * Without both, a later abort could be reported as a local cancel: by `remixId` when the next flow
	 * is an update (the update producer never announces a flow start this component can reset on), and
	 * by the refusal check when the same flow is aborted from elsewhere after its cancel was rejected.
	 */
	const abortOriginRef = useRef<{ reason: string; remixId: string } | undefined>(undefined);

	/**
	 * The flow that completed into a Rovo Chat handoff, which is a success even though the machine
	 * reads it as an abort: the conversation goes idle with an assistant response and no remix action,
	 * which is exactly `looksAborted` in `deriveRemixPhase.utils.ts`. Legacy excludes the same state
	 * through `isCustomOnlyPromptComplete`, so `isRemixAborted` never fires for it and the handoff
	 * settles its own experiences and thinking UI.
	 *
	 * Recorded so the machine-owned abort effects stand down for that flow instead of reporting an
	 * abort over the handoff's success and tearing its thinking UI down underneath it. Keyed by
	 * `remixId` for the same reason as {@link abortOriginRef}: a later flow's genuine abort must still
	 * settle.
	 */
	const handedOffRemixIdRef = useRef<string | undefined>(undefined);

	/**
	 * Settles the cancellation experiences. Owned by `onRemixFlowActiveChange` for a legacy flow, and
	 * by `RemixAborted` / `RemixFlowEnded('cancelled')` for an authoritative machine session.
	 */
	const settleCancellationExperience = useCallback(() => {
		experienceTracker.abort({
			name: ExperienceName.REMIX_START,
			reason: CANCELLATION_ABORT_REASON,
			attributes: { ...remixStartAttributesRef.current, reason: CANCELLATION_ABORT_REASON },
		});
		if (!isUpdateRef.current) {
			abortRemixCreateExperience(experienceTracker, CANCELLATION_ABORT_REASON);
		}
		remixGeneratePreviewExperience.abort({ metadata: { reason: CANCELLATION_ABORT_REASON } });
	}, [experienceTracker]);

	// remove legacy local state when cleanup platform_editor_ai_remix_in_editor_loading
	const [isComplete, setIsComplete] = useState(false);
	const resetCompletion = useCallback(() => {
		setIsComplete(false);
	}, []);

	const openPreviewAndTrack = useCallback(() => {
		experienceTracker.succeed({
			name: ExperienceName.REMIX_START,
			...(Object.keys(remixStartAttributes).length > 0 ? { attributes: remixStartAttributes } : {}),
		});
		hasCommittedRemixCreateTerminal.current = true;
		if (shouldOpenRovoChatOnCompleteRef.current) {
			shouldOpenRovoChatOnCompleteRef.current = false;
			remixGeneratePreviewExperience.success();
			if (retainedLifecycle) {
				// No machine event is emitted for this handoff and ownership is kept below, so the
				// completion write is fenced off. End the presentation directly instead.
				onDismissThinkingUi?.({ endsFlow: true, reopenModal: false });
			} else if (setThinkingComplete) {
				setThinkingComplete(true);
			} else {
				onRemixThinkingCompleteAnimationFinished?.();
				finishRemixThinking?.();
				clearThinkingDecoration?.();
			}
			handleModalClose();
			const conversationId = conversationIdRef.current;
			// The acknowledged handoff exists only to settle a retained shadow lifecycle. Without one
			// (control, master gate off, adapter absent) take the legacy path unchanged.
			if (retainedLifecycle && productIntegration.requestConversationAssistantOpen) {
				void productIntegration
					.requestConversationAssistantOpen('sidebar', conversationId)
					.then((result) => {
						if (result.outcome === 'acknowledged') {
							retainedLifecycle.end('handed-off');
							return;
						}
						// No proof of handoff: release shadow ownership as an unacknowledged handoff so it
						// cannot block the next Remix, and record the bounded outcome only when this path
						// actually released it. Not a cancellation and not a product failure: no machine
						// terminal event, no producer abort, no cancellation UX.
						if (retainedLifecycle.end('abandoned')) {
							logHandoffOutcomeBestEffort('rovo-chat', result);
						}
					})
					.catch(() => {
						if (retainedLifecycle.end('abandoned')) {
							logHandoffOutcomeBestEffort('rovo-chat', { outcome: 'rejected' });
						}
					});
			} else {
				// Legacy integrations can request the sidebar, but cannot prove that the requested
				// conversation opened. Keep lifecycle ownership until a real acknowledgement exists.
				productIntegration.openConversationAssistant?.('sidebar', conversationId);
			}
			return;
		}
		experienceTracker.start({
			name: ExperienceName.REMIX_RENDERS_IN_MODAL,
			id: ExperienceName.REMIX_RENDERS_IN_MODAL,
			attributes: {
				product: productConfig.analyticsProductName ?? productConfig.productKey,
				remixType: dynamicUiType,
				remixSubtype: selectedSubTypeRef.current,
				entryPoint: 'editor',
			},
		});
		let isExternalPreviewLifecycleActive = Boolean(isViewMode && retainedLifecycle);
		let hasAcceptedExternalPreview = false;
		openRemixPreviewRef.current({
			onOpen: retainedLifecycle
				? () => {
						const accepted = retainedLifecycle.transition({ type: 'PREVIEW_OPENED' });
						if (isViewMode && !accepted) {
							isExternalPreviewLifecycleActive = false;
							retainedLifecycle.rollback();
						}
					}
				: undefined,
			...(isViewMode && retainedLifecycle
				? {
						onSubmit: () => {
							if (!isExternalPreviewLifecycleActive) {
								return;
							}
							hasAcceptedExternalPreview = retainedLifecycle.transition({ type: 'ACCEPTED' });
							if (!hasAcceptedExternalPreview) {
								isExternalPreviewLifecycleActive = false;
								retainedLifecycle.rollback();
							}
						},
						editorInsertionAcknowledgement: {
							onAcknowledged: () => {
								if (!isExternalPreviewLifecycleActive) {
									return;
								}
								// The acknowledgement can arrive before or after the submit observer depending on the
								// finalize surface, so accept here when submit has not already done it.
								isExternalPreviewLifecycleActive = false;
								if (!hasAcceptedExternalPreview) {
									hasAcceptedExternalPreview = retainedLifecycle.transition({ type: 'ACCEPTED' });
								}
								if (
									!hasAcceptedExternalPreview ||
									!retainedLifecycle.transition({ type: 'INSERTED' })
								) {
									retainedLifecycle.rollback();
								}
							},
							onNotAcknowledged: (result) => {
								if (!isExternalPreviewLifecycleActive) {
									return;
								}
								logExternalPreviewInsertionNotAcknowledgedBestEffort(result);
								if (result.outcome !== 'timed-out-in-flight') {
									return; // recovery UI is still open; the flow stays active
								}
								// Delivered but unacknowledged: outcome unknown, so release rather than hold a possible duplicate.
								isExternalPreviewLifecycleActive = false;
								retainedLifecycle.end('abandoned');
							},
						},
						onDismiss: () => {
							if (!isExternalPreviewLifecycleActive) {
								return;
							}
							isExternalPreviewLifecycleActive = false;
							retainedLifecycle.transition({ type: 'CANCELLED' });
							retainedLifecycle.settleTerminal();
							retainedLifecycle.end('cancelled');
						},
					}
				: {}),
		});
		// stop thinking when showing the preview modal, otherwise,
		// the thinking bar will flash when the preview modal close
		if (fg('platform_editor_ai_remix_in_editor_loading')) {
			if (isViewMode && !fg('cc-maui-fix-live-doc-previews')) {
				onDismissThinkingUi?.({ reopenModal: false });
			} else {
				// `preserveRequest` suppresses only the request abort; the host still dismisses the legacy
				// thinking UI. Only an admitted lifecycle flow asks for it: a view-mode preview may still be
				// streaming into the external surface. Without admission the legacy path is unchanged.
				onStopThinking({
					reopenModal: false,
					preserveRequest: isViewMode && retainedLifecycle !== undefined,
				});
			}
		}
	}, [
		clearThinkingDecoration,
		dynamicUiType,
		experienceTracker,
		finishRemixThinking,
		handleModalClose,
		isViewMode,
		logExternalPreviewInsertionNotAcknowledgedBestEffort,
		logHandoffOutcomeBestEffort,
		onDismissThinkingUi,
		onRemixThinkingCompleteAnimationFinished,
		onStopThinking,
		productConfig.analyticsProductName,
		productConfig.productKey,
		productIntegration,
		retainedLifecycle,
		setThinkingComplete,
		remixStartAttributes,
	]);

	// when updating in editing mode, will call this callback when the thinking bar is complete
	// when in viewing mode, we need to open the preview modal when the thinking bar is complete
	const onCompleteAnimationFinished = useCallback(() => {
		if (!isViewMode && fg('platform_editor_ai_remix_toolbar')) {
			onRemixThinkingCompleteAnimationFinished?.();
		} else {
			openPreviewAndTrack();
		}
	}, [openPreviewAndTrack, onRemixThinkingCompleteAnimationFinished, isViewMode]);

	/**
	 * The completion path `RemixReady` owns for an authoritative session.
	 *
	 * Byte-for-byte the branch it replaces in `onActionReady`, so each cohort's gate behavior is
	 * unchanged. It writes no ProseMirror lifecycle: in treatment `setThinkingComplete(true)` no
	 * longer dispatches one, because the lifecycle mirror already projects `isComplete` from the
	 * machine's `ready` phase and holds `isThinking` until the completion animation finishes.
	 */
	const markReady = useCallback(() => {
		if (isRemixCancelled.current) {
			return;
		}
		if (fg('platform_editor_ai_remix_in_editor_loading') ? isViewMode : true) {
			// When we use the "showProgressBar"
			if (fg('platform_editor_maui_jira_updates')) {
				// Defer opening the preview until the progress bar fills to 100%.
				if (fg('platform_editor_ai_remix_in_editor_loading')) {
					setThinkingComplete?.(true);
				} else {
					setIsComplete(true);
				}
			} else {
				openPreviewAndTrack();
			}
		}
	}, [isViewMode, openPreviewAndTrack, setThinkingComplete]);

	const onActionReady = useCallback(
		(options?: RemixActionReadyOptions) => {
			if (!isRemixCancelled.current) {
				if (!isUpdate) {
					remixGeneratePreviewExperience.mark(
						REMIX_GENERATE_PREVIEW_MARKS.ACTION_COMPLETE_RECEIVED,
					);
				}
				if (!options?.shouldOpenRovoChat) {
					shouldOpenRovoChatOnCompleteRef.current = false;
				}
				if (options?.shouldOpenRovoChat && shouldOpenRovoChatOnCompleteRef.current) {
					// Not a machine `ready`: a custom prompt that completed without a remix action has no
					// result to preview, so `isPreviewReady` is false and no `RemixReady` is emitted for it.
					// This handoff stays legacy-driven in every cohort, and the command carries no
					// `shouldOpenRovoChat` to express it.
					//
					// The machine reads the same conversation state as an abort, so record the handoff
					// first: it is the success this flow committed, and the abort effects have to yield to
					// it. Recorded from the retained handle rather than the read session, which the owner
					// has not necessarily published yet at completion. See {@link handedOffRemixIdRef}.
					handedOffRemixIdRef.current = retainedLifecycle?.remixId;
					openPreviewAndTrack();
					return;
				}
				// `RemixReady` owns completion for an authoritative session, through `markReady`.
				if (isMachineAuthoritative) {
					return;
				}
				if (fg('platform_editor_ai_remix_in_editor_loading') ? isViewMode : true) {
					// When we use the "showProgressBar"
					if (fg('platform_editor_maui_jira_updates')) {
						// Defer opening the preview until the progress bar fills to 100%.
						if (fg('platform_editor_ai_remix_in_editor_loading')) {
							setThinkingComplete?.(true);
						} else {
							setIsComplete(true);
						}
					} else {
						openPreviewAndTrack();
					}
				}
			}
		},
		[
			isMachineAuthoritative,
			isUpdate,
			openPreviewAndTrack,
			retainedLifecycle,
			setThinkingComplete,
			isViewMode,
		],
	);

	// Invariant: legacy UX/telemetry only, for every integration. Must not settle machine ownership.
	const onStagingAIFCFlowStarted = useCallback(() => {
		experienceTracker.succeed({ name: ExperienceName.REMIX_START });
		succeedRemixCreateExperienceAtHandoff(experienceTracker);
		remixGeneratePreviewExperience.success();
		isRemixCancelled.current = true;
		clearThinkingDecoration?.();
		finishRemixThinking?.();
		handleModalClose();
	}, [clearThinkingDecoration, experienceTracker, finishRemixThinking, handleModalClose]);

	// Positive acknowledgement from the integration that the full staging surface opened.
	const onStagingPreviewOpened = useCallback(() => {
		retainedLifecycle?.end('handed-off');
	}, [retainedLifecycle]);

	// Bounds every `onOpen` for a retained flow. Ephemeral previews share the staging contract on
	// purpose: an unopened preview leaves the machine in `ready` and would strand the owner too.
	const lifecycleAwareProductIntegration = useMemo(
		() =>
			retainedLifecycle
				? {
						...productIntegration,
						openRemixPreview: (options: OpenRemixPreviewOptions) =>
							productIntegration.openRemixPreview({
								...options,
								...(options.onOpen
									? {
											onOpen: armPreviewAcknowledgement(retainedLifecycle, options.onOpen, () =>
												logHandoffOutcomeBestEffort(options.isFloating ? 'preview' : 'staging', {
													outcome: 'timed-out',
												}),
											),
										}
									: {}),
							}),
					}
				: productIntegration,
		[logHandoffOutcomeBestEffort, productIntegration, retainedLifecycle],
	);

	const {
		sendAIFCPrompt,
		stopRemixing,
		isRemixError,
		isRemixAborted,
		isRemixSafetyViolation,
		remixStoreError,
		openRemixPreview,
		closeRemixPreview,
	} = useRemixToAIFC({
		config: productConfig,
		onActionReady,
		productIntegration: lifecycleAwareProductIntegration,
		onStagingAIFCFlowStarted,
		onStagingPreviewOpened: retainedLifecycle ? onStagingPreviewOpened : undefined,
	});
	openRemixPreviewRef.current = openRemixPreview;

	/**
	 * Settles the failure experiences. Owned by the legacy error effect for a legacy flow, and by
	 * `RemixErrored` for an authoritative machine session.
	 */
	const settleFailureExperience = useCallback(
		(commandError?: Error) => {
			// The Rovo store's own message wins, so treatment reports exactly what legacy reported. A
			// command's error is only the fallback for a failure the store never described.
			const failure = remixStoreError?.content
				? new Error(remixStoreError.content)
				: (commandError ?? new Error(DEFAULT_REMIX_ERROR_MESSAGE));
			experienceTracker.fail({
				name: ExperienceName.REMIX_START,
				error: failure,
				attributes: {
					...remixStartAttributesRef.current,
					errorType: remixStoreError?.errorType,
					traceId: remixStoreError?.traceId,
				},
			});
			remixGeneratePreviewExperience.failure({
				metadata: {
					errorType: remixStoreError?.errorType,
					traceId: remixStoreError?.traceId,
				},
			});
		},
		[experienceTracker, remixStoreError],
	);

	/** The error path's preview close, with the visibility and view-mode conditions it always had. */
	const closeRemixPreviewForError = useCallback(() => {
		if (
			productIntegration.isRemixPreviewVisible &&
			(fg('platform_editor_ai_remix_in_editor_loading') ? isViewMode : true)
		) {
			closeRemixPreview();
		}
	}, [closeRemixPreview, isViewMode, productIntegration]);

	/**
	 * The editor UX that command handlers drive. They are mounted on a host above this component
	 * — which terminal cleanup unmounts — so they reach these closures through the registry rather
	 * than through props.
	 */
	const editorRemixUxHandles = useMemo<EditorRemixUxHandles>(
		() => ({
			closeModal: handleModalClose,
			closeTerminalPreview: closeRemixPreviewForError,
			markReady,
			openPreview: openPreviewAndTrack,
			openSidebar: () => productIntegration.toggleConversationAssistant?.('sidebar'),
			settleCancellation: settleCancellationExperience,
			settleExperience: (outcome): EditorRemixExperienceSettlement => {
				if (outcome.kind === 'failed') {
					settleFailureExperience(outcome.error);
					return 'settled';
				}
				// A flow that handed off to Rovo Chat already succeeded and tore its own thinking UI
				// down. The machine reads that same state as an abort, so this abort is not a real one
				// and neither is the teardown the caller would run after it.
				if (handedOffRemixIdRef.current === outcome.remixId) {
					return 'ignored';
				}
				// `RemixAborted` cannot tell a local cancel from a Rovo-side abort, so this flow's own
				// recorded intent wins over the command's neutral reason. Matched against the command's
				// own flow rather than the read session, which a flow that already ended has released.
				const recorded = abortOriginRef.current;
				const reason =
					recorded !== undefined && recorded.remixId === outcome.remixId
						? recorded.reason
						: outcome.reason;
				experienceTracker.abort({
					name: ExperienceName.REMIX_START,
					reason,
					attributes: { ...remixStartAttributesRef.current, reason },
				});
				if (!isUpdateRef.current) {
					abortRemixCreateExperience(experienceTracker, reason);
				}
				remixGeneratePreviewExperience.abort({ metadata: { reason } });
				return 'settled';
			},
			stopThinking: onStopThinking,
		}),
		[
			closeRemixPreviewForError,
			experienceTracker,
			handleModalClose,
			markReady,
			onStopThinking,
			openPreviewAndTrack,
			productIntegration,
			settleCancellationExperience,
			settleFailureExperience,
		],
	);

	useEffect(
		() => onRegisterEditorRemixUxHandles?.(editorRemixUxHandles),
		[editorRemixUxHandles, onRegisterEditorRemixUxHandles],
	);

	// Register stopRemixing so that cancelRemixThinking (called from the decoration or
	// RemixContent.tsx's onStopThinking for the toolbar ON path) can abort the in-flight
	// AI request and trigger the paired aiInteraction initiated + dismissed analytics.
	useEffect(() => {
		return onRegisterAbortRequest?.(stopRemixing);
	}, [onRegisterAbortRequest, stopRemixing]);

	const { updateRemix } = useUpdateRemix();
	updateRemixRef.current = updateRemix;

	const experienceTrackerRef = useRef(experienceTracker);
	experienceTrackerRef.current = experienceTracker;
	useEffect(() => {
		return () => {
			if (isRemixCancelled.current) {
				return;
			}
			experienceTrackerRef.current.abort({
				name: ExperienceName.REMIX_START,
				reason: 'User closed remix overlay',
				attributes: {
					...remixStartAttributesRef.current,
					reason: 'User closed remix overlay',
				},
			});
			if (!isUpdateRef.current && !hasCommittedRemixCreateTerminal.current) {
				abortRemixCreateExperience(experienceTrackerRef.current, 'User closed remix overlay');
			}
		};
	}, []);

	const onRemixFlowActiveChange = useCallback(
		(active: boolean) => {
			if (fg('platform_editor_ai_remix_in_editor_loading')) {
				// when start/cancel, reset the context
				resetContext();
			}

			if (active) {
				isRemixCancelled.current = false;
				shouldOpenRovoChatOnCompleteRef.current = false;
				conversationIdRef.current = undefined;
				// A new flow has not been cancelled, so it must not inherit the previous flow's intent:
				// its own abort would otherwise be settled as a local cancellation.
				abortOriginRef.current = undefined;
				// Clear stale completion state from any previous flow.
				if (!fg('platform_editor_ai_remix_in_editor_loading')) {
					resetCompletion();
				}

				// When isThinking is already true (e.g. pre-supplied userPrompt flow from
				// startRemixThinking), skip calling onStartThinking again — it would wipe
				// remixModalMetadata from plugin state by dispatching a new startRemixThinking
				// without the metadata. Use ref to avoid stale closure.
				if (!isThinkingRef.current) {
					onStartThinking(
						fg('platform_editor_ai_remix_in_editor_loading')
							? {
									contentType: productIntegration.contentType,
								}
							: undefined,
					);
				}
				return;
			}

			isRemixCancelled.current = true;
			shouldOpenRovoChatOnCompleteRef.current = false;
			conversationIdRef.current = undefined;
			if (retainedLifecycle) {
				// `RemixAborted` is origin-blind, so record this cancellation's reason before driving the
				// machine: `CANCELLED` reaches `aborted`, and the command that settles the experience has
				// no way to tell a local cancel from a Rovo-side abort.
				abortOriginRef.current = {
					reason: CANCELLATION_ABORT_REASON,
					remixId: retainedLifecycle.remixId,
				};
				// Abort the request, publish the typed intent, run the unchanged legacy cohort UX below,
				// then each exit settles the handle last.
				stopRemixing();
				if (!retainedLifecycle.transition({ type: 'CANCELLED' })) {
					// The machine refused this cancellation, so it emits no `RemixAborted` for it. Any abort
					// that does arrive for this flow came from somewhere else and must not be reported as a
					// local cancel.
					abortOriginRef.current = undefined;
				}
			}
			// Experience settlement is the one part of this path a command now owns: `RemixAborted`
			// settles it through `settleExperience`, and a `cancelled` teardown that no terminal command
			// settled falls through to `settleCancellation`. The teardown below stays here because it is
			// user-initiated, not machine-derived.
			if (!isMachineAuthoritative) {
				settleCancellationExperience();
			}

			if (
				expValEqualsNoExposure('cc_page_experiences_editor_image_generation', 'isEnabled', true)
			) {
				const userPrompt = remixModalMetadataRef.current?.userPrompt;
				const onCancel = remixModalMetadataRef.current?.onCancel;
				if (onCancel && userPrompt) {
					// Abort the AI request so send-message-action.ts fires the paired
					// aiInteraction initiated + dismissed events automatically via AbortError.
					if (!retainedLifecycle) {
						stopRemixing();
					}
					onStopThinking({ reopenModal: false });
					onCancel();
					retainedLifecycle?.settleTerminal();
					retainedLifecycle?.end('cancelled');
					return;
				}
			}

			if (!retainedLifecycle) {
				stopRemixing();
			}
			if (fg('platform_editor_ai_remix_in_editor_loading') ? isViewMode : true) {
				onStopThinking({ reopenModal: false });
			}
			retainedLifecycle?.settleTerminal();
			retainedLifecycle?.end('cancelled');
		},
		[
			isViewMode,
			isMachineAuthoritative,
			onStartThinking,
			onStopThinking,
			productIntegration.contentType,
			resetCompletion,
			settleCancellationExperience,
			stopRemixing,
			resetContext,
			retainedLifecycle,
		],
	);

	const onRemixFlowActiveChangeRef = useRef(onRemixFlowActiveChange);
	useEffect(() => {
		onRemixFlowActiveChangeRef.current = onRemixFlowActiveChange;
	}, [onRemixFlowActiveChange]);

	useEffect(() => {
		if (!retainedLifecycle) {
			return;
		}
		// The product registry fences this callback to the retained session.
		return onRegisterEditorRemixMutationCancellationSink?.(() => {
			onRemixFlowActiveChangeRef.current(false);
			return true;
		});
	}, [onRegisterEditorRemixMutationCancellationSink, retainedLifecycle]);

	// When thinking stops without completing, treat it as cancellation.
	const previousIsThinking = useRef(false);
	useEffect(() => {
		const isImageGenFlow =
			expValEqualsNoExposure('cc_page_experiences_editor_image_generation', 'isEnabled', true) &&
			!!remixModalMetadataRef.current?.userPrompt;
		const shouldFire =
			(!isImageGenFlow && fg('platform_editor_ai_remix_in_editor_loading')) ||
			(isImageGenFlow && fg('platform_editor_ai_remix_toolbar'));
		if (
			!isViewMode &&
			previousIsThinking.current &&
			!isThinking &&
			pluginIsComplete !== true &&
			showLocationPicker !== true &&
			!isRemixCancelled.current &&
			shouldFire
		) {
			// The authority latch outlives the session, so this also catches a flow already released,
			// e.g. handed off to Rovo Chat before thinking fell.
			if (retainedLifecycleRef.current || isMachineAuthoritativeRef.current) {
				try {
					editorRemixLifecycleAdapterRef.current?.observeLifecycle?.({
						actionSubjectId: 'cancellationSignal',
						producer: isUpdateRef.current ? 'variant-update' : 'create',
						artifactType: 'unknown',
						outcome: 'thinking-fell',
						reason: retainedLifecycleRef.current ? 'retained-flow' : 'released-authoritative-flow',
					});
				} catch {
					// Observability must never affect cancellation behavior.
				}
			}
			onRemixFlowActiveChangeRef.current(false);
		}
		previousIsThinking.current = isThinking;
	}, [isThinking, pluginIsComplete, showLocationPicker, isViewMode]);

	// Dismiss the thinking bar when the user stops the remix from the Rovo side chat.
	// `RemixAborted` owns abort settlement and thinking cleanup for an authoritative session; the
	// bookkeeping above it is untouched so both cohorts read the same internal state.
	useEffect(() => {
		if (isMachineAuthoritative) {
			return;
		}
		if (isRemixAborted && !isRemixCancelled.current) {
			experienceTracker.abort({
				name: ExperienceName.REMIX_START,
				reason: REMOTE_ABORT_REASON,
				attributes: remixStartAttributes,
			});
			if (!isUpdate) {
				abortRemixCreateExperience(experienceTracker, REMOTE_ABORT_REASON);
			}
			remixGeneratePreviewExperience.abort({ metadata: { reason: REMOTE_ABORT_REASON } });
			onStopThinking({ reopenModal: false });
		}
	}, [
		experienceTracker,
		isMachineAuthoritative,
		isRemixAborted,
		isUpdate,
		onStopThinking,
		remixStartAttributes,
	]);

	// Fail experience and clean up thinking UI when Rovo encounters an error.
	// `RemixErrored` owns failure settlement and error cleanup for an authoritative session. The
	// `previousIsRemixError` edge tracking below stays unfenced so both cohorts keep the same
	// internal state.
	const previousIsRemixError = useRef(isRemixError);
	useEffect(() => {
		if (
			!isMachineAuthoritative &&
			!previousIsRemixError.current &&
			isRemixError &&
			!isRemixCancelled.current
		) {
			settleFailureExperience();
			onStopThinking({ reopenModal: true });
		}
		previousIsRemixError.current = isRemixError;
	}, [isMachineAuthoritative, isRemixError, onStopThinking, settleFailureExperience]);

	// When an error occurs, close the ephemeral preview if open, dismiss the
	// remix UI entirely, and open the sidebar so the user can see the error.
	// Fenced as one unit with the effect above: `RemixErrored` closes the preview, opens the sidebar
	// and closes the modal in the same handler, before it dismisses the latched terminal session.
	useEffect(() => {
		if (!isMachineAuthoritative && !isRemixCancelled.current && isRemixError) {
			closeRemixPreviewForError();

			// Use handleModalClose (not onStopThinking) to fully unmount the component
			// and clear isModalOpen. The first error effect already sets reopenModal:true
			// via onStopThinking; using it here alone would leave the progress bar visible.
			handleModalClose();
			productIntegration.toggleConversationAssistant?.('sidebar');
		}
	}, [
		closeRemixPreviewForError,
		handleModalClose,
		isMachineAuthoritative,
		isRemixError,
		productIntegration,
	]);

	// [MAUI-1070] Safety/AUP violation handled non-fatally: the friendly response is
	// still streaming into the conversation, but in this flow the violation looks like
	// a completed action — so the thinking bar would hang ("Almost done.") and the
	// response would stay unseen. Dismiss the thinking bar, close any preview, and open
	// the Rovo side chat so the user sees the response as it arrives.
	//
	// IMPORTANT: use `onDismissThinkingUi` (UI-only) rather than `onStopThinking` here —
	// `onStopThinking` aborts the in-flight AI request (getAbortRequest), which would
	// cut off the friendly response before it streams in and leave an empty message.
	const previousIsRemixSafetyViolation = useRef(isRemixSafetyViolation);
	useEffect(() => {
		if (
			!previousIsRemixSafetyViolation.current &&
			isRemixSafetyViolation &&
			!isRemixCancelled.current
		) {
			// Settle the expected refusal before dismissing UI. Dismissal can synchronously unmount
			// this component, whose cleanup otherwise wins with "User closed remix overlay".
			isRemixCancelled.current = true;
			hasCommittedRemixCreateTerminal.current = true;
			experienceTracker.succeed({
				name: ExperienceName.REMIX_START,
				attributes: {
					...remixStartAttributesRef.current,
					outcome: REMIX_IMAGE_GENERATION_SAFETY_REFUSAL_OUTCOME,
				},
			});
			if (!isUpdateRef.current) {
				succeedRemixCreateExperienceForSafetyRefusal(experienceTracker);
			}
			if (productIntegration.isRemixPreviewVisible) {
				closeRemixPreview();
			}
			onDismissThinkingUi?.({ reopenModal: false });
			productIntegration.toggleConversationAssistant?.('sidebar');
		}
		previousIsRemixSafetyViolation.current = isRemixSafetyViolation;
	}, [
		closeRemixPreview,
		experienceTracker,
		isRemixSafetyViolation,
		onDismissThinkingUi,
		productIntegration,
	]);

	const handleRemixOverlayClose = useCallback(() => {
		if (retainedLifecycle) {
			stopRemixing();
			retainedLifecycle.transition({ type: 'CANCELLED' });
			handleModalClose();
			retainedLifecycle.settleTerminal();
			retainedLifecycle.end('cancelled');
			return;
		}
		handleModalClose();
	}, [handleModalClose, retainedLifecycle, stopRemixing]);

	// Report the editor's own lifecycle phase for migration shadow parity. Strictly observational:
	// it projects signals computed above and never feeds back into the flow. `observeLegacyPhase` is
	// absent for products that do not observe parity, so this costs those products nothing.
	//
	// Insertion is reported by `useOnComplete` at the moment it starts, because the preview closes
	// before insertion in the editor path and no reactive signal marks it. The shared ref keeps both
	// report sites agreeing on precedence rather than racing to overwrite each other.
	const lastReportedLegacyPhaseRef = useRef<EditorRemixLifecyclePhase | undefined>(undefined);
	useEffect(() => {
		const observeLegacyPhase = editorRemixLifecycleAdapter?.observeLegacyPhase;
		if (!observeLegacyPhase) {
			return;
		}

		const legacyPhase = deriveLegacyEditorRemixPhase({
			isThinking,
			isReady: fg('platform_editor_ai_remix_in_editor_loading') ? !!pluginIsComplete : isComplete,
			isPreviewVisible: productIntegration.isRemixPreviewVisible,
			isInserting: isRemixInsertingRef.current,
			// A safety/AUP violation is an error outcome for the flow even though it arrives as a
			// completed action, so it is not a separate phase.
			isError: isRemixError || isRemixSafetyViolation,
			isAborted: isRemixAborted,
			isCancelled: isRemixCancelled.current,
		});

		if (lastReportedLegacyPhaseRef.current === legacyPhase) {
			return;
		}
		lastReportedLegacyPhaseRef.current = legacyPhase;
		observeLegacyPhase(legacyPhase);
	}, [
		editorRemixLifecycleAdapter,
		isComplete,
		isRemixAborted,
		isRemixError,
		isRemixInsertingRef,
		isRemixSafetyViolation,
		isThinking,
		pluginIsComplete,
		productIntegration.isRemixPreviewVisible,
	]);

	const constructDynamicUiSource = useCallback(async (): Promise<DynamicUiSource | undefined> => {
		const localIds = selectedContext?.localIds ?? [];
		const startLocalId = localIds[0] ?? '';
		const endLocalId = localIds[localIds.length - 1] ?? '';
		const fragmentAdf = prepareRemixFragmentNodes(
			selectedContext?.selectionFragment ?? null,
			productConfig.isNodeUnsupportedForRemix,
		);

		let fullAdf: DocNode = { type: 'doc', version: 1, content: [] };
		if (editorApi) {
			fullAdf = await new Promise((resolve) => {
				try {
					editorApi.core?.actions?.requestDocument?.(
						(doc?: JSONDocNode) => {
							resolve((doc as DocNode | undefined) ?? { type: 'doc', version: 1, content: [] });
						},
						{ alwaysFire: true },
					);
				} catch {
					resolve({ type: 'doc', version: 1, content: [] });
				}
			});
		}

		// When the entire page is selected (e.g. cmd+a), selectionFragment is null
		// because ProseMirror's AllSelection does not produce a fragment. Fall back
		// to the full document content so Remix still receives the source it needs.
		const resolvedFragmentAdf = fragmentAdf ?? fullAdf.content;

		return {
			contentType: productIntegration.contentType,
			startLocalId,
			endLocalId,
			startIndex: selectedContext?.startIndex ?? 0,
			endIndex: selectedContext?.endIndex ?? -1,
			fragmentAdf: resolvedFragmentAdf,
			fullAdf,
		};
	}, [
		editorApi,
		productIntegration.contentType,
		selectedContext,
		productConfig.isNodeUnsupportedForRemix,
	]);

	/**
	 * Entry points start REMIX_START (and, for creates, the create collect) before either producer
	 * runs, so a refused flow has to settle them or they stay open until unmount and get reported as
	 * a user action. `isRemixCancelled` stops the unmount and thinking-stopped effects doing that.
	 */
	const settleRefusedAdmission = useCallback(() => {
		isRemixCancelled.current = true;
		hasCommittedRemixCreateTerminal.current = true;
		experienceTracker.abort({
			name: ExperienceName.REMIX_START,
			reason: 'active-flow-rejected',
			attributes: { ...remixStartAttributes, reason: 'active-flow-rejected' },
		});
		if (!isUpdate) {
			abortRemixCreateExperience(experienceTracker, 'active-flow-rejected');
		}
	}, [experienceTracker, isUpdate, remixStartAttributes]);

	const onOptionSelect = useCallback(
		async ({ type, subType, userPrompt }: RemixOptionSelectionParameters) => {
			const isRovoChatSubmission =
				remixSource === 'rovoChat' && remixModalMetadata?.preselectedConversationId !== undefined;
			const isCustomOnlyPrompt = type === 'custom' && !subType && !!userPrompt?.trim();
			// Toolbar / Rovo selections are submitted by `RemixContainer`'s own auto-submit, and it
			// renders no picker for them, so reopening would show an empty surface.
			const isPreselectedSubmission = remixModalMetadata?.preselectedType !== undefined;

			// Built before admission so nothing is awaited between the machine write and the send.
			let dynamicUiSource = await constructDynamicUiSource();
			// When the AI Image Generation media-insert tab is the entry point there is
			// no editor selection - the user's typed prompt IS the "highlighted text".
			// Mirror the shape the existing "click Image in Visual aid accordion" flow
			// produces (a doc-ADF with the source content) so the backend image-generation
			// path receives the prompt via dynamicUiSource.fragmentAdf / fullAdf the same
			// way it normally receives selected text.
			// remixModalMetadataRef.current?.userPrompt is only set by the media-insert
			// image-generation flow (not the Remix modal). It distinguishes "prompt
			// pre-supplied by media-insert tab" from "prompt typed in the Remix overlay."
			if (type === 'image' && userPrompt && remixModalMetadataRef.current?.userPrompt) {
				const promptDoc: DocNode = {
					type: 'doc',
					version: 1,
					content: [
						{
							type: 'paragraph',
							content: [{ type: 'text', text: userPrompt }],
						},
					],
				};
				dynamicUiSource = {
					contentType: dynamicUiSource?.contentType ?? productIntegration.contentType,
					startLocalId: dynamicUiSource?.startLocalId ?? '',
					endLocalId: dynamicUiSource?.endLocalId ?? '',
					startIndex: dynamicUiSource?.startIndex ?? 0,
					endIndex: dynamicUiSource?.endIndex ?? -1,
					fragmentAdf: promptDoc.content,
					fullAdf: promptDoc,
				};
			}

			// Every create entrypoint converges here: one lifecycle write, no per-entrypoint gate policy.
			// Admission is asked for before any editor state or telemetry side effect, so a refused flow
			// leaves nothing started behind it.
			const lifecycleRetention = admitAndRetainEditorRemixLifecycle({
				adapter: editorRemixLifecycleAdapter,
				owner: lifecycleOwner,
				request: {
					kind: 'create',
					observation: {
						producer: 'create',
						artifactType: type === 'image' ? 'image' : type === 'diagram' ? 'whiteboard' : 'maui',
					},
					// Omitted for a brand-new create so the machine allocates the authoritative
					// conversation identity; supplied when this flow continues a Rovo Chat conversation.
					...(remixModalMetadata?.preselectedConversationId !== undefined && {
						conversationId: remixModalMetadata.preselectedConversationId,
					}),
				},
			});

			// Fail closed for product policy only; every other outcome falls through to the legacy request.
			if (lifecycleRetention.status === 'active-flow-rejected') {
				// Thinking is already on when this runs, so settle the open experiences and dismiss it
				// (UI-only, so no request is aborted). Re-arm the guard so a refused prompt can retry.
				settleRefusedAdmission();
				hasAutoSubmittedRef.current = false;
				onDismissThinkingUi?.({ reopenModal: !isPreselectedSubmission });
				return;
			}

			// Possession of the handle IS the treatment decision for this flow. Nothing below may
			// re-evaluate rollout controls for it.
			const admittedLifecycle =
				lifecycleRetention.status === 'retained' ? lifecycleRetention.lifecycle : undefined;

			// Everything from here to the send is synchronous, so a throw would leave the admitted
			// machine active with no request for the watcher to settle, blocking later creates.
			let sendResult: Promise<void>;
			try {
				if (
					!isViewMode &&
					UNSAFE_expValNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'control') ===
						'variant2'
				) {
					closeConversationAssistant?.();
				}
				if (fg('platform_editor_ai_remix_in_editor_loading')) {
					setDynamicUiType(type);
					setDynamicUiSubtype(subType);
					setInteractionSource(remixModalMetadata?.interactionSource);
					setAspectRatio(remixModalMetadata?.aspectRatio);
				}
				selectedSubTypeRef.current = subType;
				conversationIdRef.current = undefined;
				shouldOpenRovoChatOnCompleteRef.current =
					!isRovoChatSubmission &&
					isCustomOnlyPrompt &&
					isExperimentEnabled('cc_maui_polish_changes_batch_5');
				const featureArea = getRemixFeatureArea({ remixType: type });
				experienceTracker.updateAttributes({
					name: ExperienceName.REMIX_START,
					attributes: {
						remixType: type,
						remixSubtype: subType,
						includesFreegenPrompt: !!userPrompt,
						...(featureArea !== undefined && { featureArea }),
					},
				});

				remixGeneratePreviewExperience.start();
				remixGeneratePreviewExperience.mark(REMIX_GENERATE_PREVIEW_MARKS.SUBMIT);
				remixGeneratePreviewExperience.addMetadata({
					product: productConfig.analyticsProductName ?? productConfig.productKey,
					interactionSource: remixModalMetadata?.interactionSource,
					remixType: type,
					remixSubtype: subType,
					entryPoint: isViewMode ? 'renderer' : 'editor',
					contentType: productIntegration.contentType,
					includesFreegenPrompt: !!userPrompt,
					...getRemixGeneratePreviewMetadata?.({ type, subType, userPrompt }),
				});

				if (fg('platform_editor_ai_remix_in_editor_loading')) {
					setDynamicUiSource(dynamicUiSource);
				}

				sendResult = sendAIFCPrompt({
					userPrompt: isRovoChatSubmission ? undefined : userPrompt,
					promptText: isRovoChatSubmission ? userPrompt : undefined,
					dynamicUiType: type,
					dynamicUiSubtype: subType,
					dynamicUiSource,
					interactionSource: remixModalMetadata?.interactionSource,
					aspectRatio: remixModalMetadata?.aspectRatio,
					aiFeatureName: remixModalMetadata?.aiFeatureName,
					conversationId: remixModalMetadata?.preselectedConversationId,
					lifecycleConversationId: admittedLifecycle?.conversationId,
					// See comment above — metadata userPrompt is the media-insert flow signal
					isMediaInsertFlow: !!remixModalMetadataRef.current?.userPrompt,
				}).then((conversationId) => {
					conversationIdRef.current = conversationId;
				});
			} catch (error) {
				admittedLifecycle?.rollback();
				throw error;
			}

			// Release the machine on a rejected send, or the single-active policy blocks the next create.
			if (admittedLifecycle) {
				void sendResult.catch(() => {
					admittedLifecycle.rollback();
				});
			}
		},
		[
			editorRemixLifecycleAdapter,
			lifecycleOwner,
			onDismissThinkingUi,
			settleRefusedAdmission,
			setDynamicUiType,
			setDynamicUiSubtype,
			setInteractionSource,
			setAspectRatio,
			constructDynamicUiSource,
			closeConversationAssistant,
			experienceTracker,
			isViewMode,
			getRemixGeneratePreviewMetadata,
			productConfig.analyticsProductName,
			productConfig.productKey,
			productIntegration.contentType,
			remixModalMetadata,
			remixSource,
			sendAIFCPrompt,
			setDynamicUiSource,
		],
	);

	// When a userPrompt is pre-supplied in remixModalMetadata (e.g. from the AI Image Generation
	// submit flow), skip the option-picker modal UI and go straight to processing.
	// The ref guard prevents double-firing if the component re-renders.
	// Must be placed before any early returns to satisfy React hooks rules.
	useEffect(() => {
		const isSuggestedRemix =
			remixSource === 'suggestedRemix' &&
			UNSAFE_expValNoExposure('platform_editor_ai_content_suggested_edits', 'isEnabled', false);
		if (
			!isSuggestedRemix &&
			!expValEqualsNoExposure('cc_page_experiences_editor_image_generation', 'isEnabled', true)
		) {
			return;
		}
		if (hasAutoSubmittedRef.current) {
			return;
		}
		const userPrompt = remixModalMetadata?.userPrompt;
		if (!userPrompt) {
			return;
		}
		hasAutoSubmittedRef.current = true;
		onRemixFlowActiveChange(true);
		const autoSubmitType = remixModalMetadata?.dynamicUiType ?? 'custom';
		// Suggested Remix drives the subtype via `dynamicUiSubtype`; other flows (media-insert image
		// generation) use `imageStyle`. Falls back to "image" so the backend always gets a subtype.
		const suggestedSubtype = isSuggestedRemix ? remixModalMetadata?.dynamicUiSubtype : undefined;
		const autoSubmitSubType = suggestedSubtype ?? remixModalMetadata?.imageStyle ?? 'image';
		void onOptionSelect({ type: autoSubmitType, subType: autoSubmitSubType, userPrompt });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [remixModalMetadata?.userPrompt]);

	// When the editor toolbar "Remix" dropdown is the picker, it opens the chooser
	// with a preselected option in metadata. The RemixContainer suppresses its own
	// picker UI and starts the flow immediately for this selection (see the
	// `autoSubmitSelection` prop passed below).
	const autoSubmitSelection =
		!isUpdate && remixModalMetadata?.preselectedType
			? {
					type: remixModalMetadata.preselectedType,
					subType: remixModalMetadata.preselectedSubType,
					...(remixModalMetadata.preselectedUserPrompt && {
						userPrompt: remixModalMetadata.preselectedUserPrompt,
					}),
				}
			: undefined;

	const submittedUpdateKeyRef = useRef<string | undefined>(undefined);
	// Bound ownership to the lifecycle attempt this effect admitted. A preset key is only a request
	// description: it changes when the user picks another subtype, while the retained lifecycle is the
	// authority that decides whether that request belongs to this effect or an inline Rovo flow.
	const locallyOwnedUpdateProvenanceRef = useRef<
		| {
				updateKey: string;
				lifecycleToken: RetainedEditorRemixLifecycle['token'];
				remixId: string;
		  }
		| undefined
	>(undefined);
	// The stale preset remains consumed for this update epoch even if the inline lifecycle releases
	// before the host clears `isThinking`. Bug 2 can use this state for presentation arbitration
	// without having to infer ownership a second time.
	const externallyOwnedPresetUpdateKeyRef = useRef<string | undefined>(undefined);
	const [externallyOwnedPresetUpdateKey, setExternallyOwnedPresetUpdateKey] = useState<
		string | undefined
	>(undefined);
	useEffect(() => {
		if (!isViewMode && fg('platform_editor_ai_remix_toolbar')) {
			const isOwnershipArbitrationEnabled = isEditorRemixStateRearchitectureTreatment === true;
			if (!isOwnershipArbitrationEnabled) {
				// Control retains the pre-migration submission path. Do not let an earlier treatment
				// epoch leak ownership into a later treatment render, but preserve submitted-key behavior.
				locallyOwnedUpdateProvenanceRef.current = undefined;
				if (externallyOwnedPresetUpdateKeyRef.current !== undefined) {
					externallyOwnedPresetUpdateKeyRef.current = undefined;
					setExternallyOwnedPresetUpdateKey(undefined);
				}
			}
			const localProvenance = locallyOwnedUpdateProvenanceRef.current;
			if (
				isOwnershipArbitrationEnabled &&
				localProvenance &&
				retainedLifecycle?.token !== localProvenance.lifecycleToken
			) {
				// The lifecycle that made this effect local is gone or has been replaced. Do not retain
				// ownership through a transient thinking state: a later lifecycle must prove itself again.
				locallyOwnedUpdateProvenanceRef.current = undefined;
			}
			if (!isThinking || !isUpdate) {
				submittedUpdateKeyRef.current = undefined;
				if (
					isOwnershipArbitrationEnabled &&
					externallyOwnedPresetUpdateKeyRef.current !== undefined
				) {
					externallyOwnedPresetUpdateKeyRef.current = undefined;
					setExternallyOwnedPresetUpdateKey(undefined);
				}
				return;
			}
			if (
				isOwnershipArbitrationEnabled &&
				externallyOwnedPresetUpdateKeyRef.current !== undefined
			) {
				// This thinking epoch was already identified as an inline-owned stale preset. A subtype
				// change is still stale state, not permission to replay an update after that lifecycle
				// releases before the host clears its projection.
				return;
			}
			const { shouldSubmit, updateKey } = getUpdateRemixSubmissionDecision(
				historyEntryForUpdate,
				dynamicUiSubtypeForUpdate,
				submittedUpdateKeyRef.current,
			);
			if (shouldSubmit && updateKey) {
				if (isOwnershipArbitrationEnabled && retainedLifecycle) {
					if (retainedLifecycle.token !== locallyOwnedUpdateProvenanceRef.current?.lifecycleToken) {
						// A stale preset can remain after inline Rovo has retained a lifecycle. Do not send
						// or run refusal cleanup against that different attempt. Consume this update key so
						// a release before the host clears thinking cannot replay the stale request.
						submittedUpdateKeyRef.current = updateKey;
						if (externallyOwnedPresetUpdateKeyRef.current !== updateKey) {
							externallyOwnedPresetUpdateKeyRef.current = updateKey;
							setExternallyOwnedPresetUpdateKey(updateKey);
						}
						return;
					}
					// A new subtype is a new request, but the previous locally-admitted request still
					// owns the single-flow slot. Leave the key unclaimed so this effect retries after its
					// own lifecycle settles, rather than misclassifying it as external or refusing it.
					return;
				}
				submittedUpdateKeyRef.current = updateKey;
				const updateOutcome = updateRemixRef.current?.();
				if (isOwnershipArbitrationEnabled && updateOutcome && typeof updateOutcome !== 'string') {
					locallyOwnedUpdateProvenanceRef.current = {
						updateKey,
						lifecycleToken: updateOutcome.lifecycle.token,
						remixId: updateOutcome.lifecycle.remixId,
					};
				}
				if (updateOutcome === 'active-flow-rejected') {
					// Leave `submittedUpdateKeyRef` set or this re-attempts every render; the `!isThinking`
					// branch above clears it once the dismissal lands, which is what allows a retry.
					settleRefusedAdmission();
					onDismissThinkingUi?.({ reopenModal: false });
				}
			}
		}
	}, [
		isThinking,
		isUpdate,
		isEditorRemixStateRearchitectureTreatment,
		historyEntryForUpdate,
		dynamicUiSubtypeForUpdate,
		isViewMode,
		onDismissThinkingUi,
		retainedLifecycle,
		settleRefusedAdmission,
	]);

	// Render nothing while no remix UI belongs on screen, but stay mounted. Every hook above keeps
	// running, which is the point: this subtree owns the conversation read that produces
	// `finalMessage`. Placed after the last hook so the early return cannot change hook order.
	if (!isUiVisible) {
		return null;
	}

	if (!isViewMode && showLocationPicker && fg('platform_editor_ai_remix_in_editor_loading')) {
		return <LocationPickerPopup />;
	}

	// Create flows own their editor thinking presentation. For updates, only the stale preset that
	// Bug 1 identified as externally owned yields its fixed bar to the legitimate inline Rovo owner.
	const hideThinkingBar =
		!isViewMode &&
		isThinking &&
		(!isUpdate ||
			(isEditorRemixStateRearchitectureTreatment === true &&
				externallyOwnedPresetUpdateKey !== undefined));

	return (
		<>
			<RemixContainer
				remixAttemptId={remixAttemptId}
				hideThinkingBar={fg('platform_editor_ai_remix_toolbar') ? hideThinkingBar : undefined}
				showProgressBar={
					fg('platform_editor_maui_jira_updates') &&
					(!fg('platform_editor_ai_remix_in_editor_loading') || isViewMode)
				}
				isComplete={
					fg('platform_editor_ai_remix_in_editor_loading') ? pluginIsComplete : isComplete
				}
				isThinking={fg('platform_editor_ai_remix_in_editor_loading') ? isThinking : undefined}
				onCompleteAnimationFinished={
					fg('platform_editor_ai_remix_toolbar')
						? !hideThinkingBar
							? onCompleteAnimationFinished
							: undefined
						: onCompleteAnimationFinished
				}
				onClose={handleRemixOverlayClose}
				onOptionSelect={onOptionSelect}
				onRemixFlowActiveChange={onRemixFlowActiveChange}
				isEphemeralViewVisible={productIntegration.isRemixPreviewVisible}
				hiddenOptions={hiddenOptions}
				contentType={productIntegration.contentType}
				autoSubmitUserPrompt={remixModalMetadata?.userPrompt ?? undefined}
				autoSubmitSelection={autoSubmitSelection}
				onStopThinking={onStopThinking}
				anchorRect={remixModalMetadata?.anchorRect as DOMRect | undefined}
				popupPlacement={remixModalMetadata?.popupPlacement ?? 'bottom-start'}
				isRemixEntrance={
					expValEqualsNoExposure('cc_page_experiences_editor_image_generation', 'isEnabled', true)
						? !remixModalMetadata?.userPrompt
						: true
				}
			/>
		</>
	);
};

export const RemixContentComponent = <
	TEditorApi extends EditorApiWithCorePlugin = EditorApiWithCorePlugin,
>(
	props: RemixContentComponentFullProps<TEditorApi>,
): React.JSX.Element | null => {
	// `startRemixThinking` closes the options modal (`isRemixModalOpen` false) while remix
	// is still active. We must keep rendering `RemixContainer` (thinking bar / ephemeral)
	// whenever the editor reports `isThinking`, not only when the modal is open.
	// Whether any remix UI belongs on screen. `startRemixThinking` closes the options modal
	// (`isRemixModalOpen` false) while remix is still active, so `isThinking` has to keep the
	// container rendered too, not only an open modal.
	const isRemixUiVisible =
		props.isModalOpen ||
		props.isThinking ||
		(props.showLocationPicker && fg('platform_editor_ai_remix_in_editor_loading'));

	// `hasLiveMachineFlow` covers a window neither flag can see: the machine projection turns
	// `isThinking` off when the early streaming whiteboard is placed, while generation continues.
	// The subtree below owns the conversation read that produces `finalMessage`, so it has to stay
	// mounted across that window or the final message lands with nothing listening and the insert
	// is never finalized. It renders `null` there rather than unmounting, which keeps the option
	// picker off screen without dropping the subscription.
	if (!isRemixUiVisible && !props.hasLiveMachineFlow) {
		return null;
	}

	const inner = fg('platform_editor_ai_remix_in_editor_loading') ? (
		<FlagsProvider>
			<EditorRemixContextProvider
				editorRemixLifecycleAdapter={props.editorRemixLifecycleAdapter}
				isEditorRemixStateRearchitectureTreatment={props.isEditorRemixStateRearchitectureTreatment}
				isViewMode={props.isViewMode}
				isRemixPreviewVisible={props.productIntegration.isRemixPreviewVisible}
				clearThinkingDecoration={props.clearThinkingDecoration}
				onBeforeEditorRemixResultInsertion={props.onBeforeEditorRemixResultInsertion}
				replaceWhiteboardEmbed={props.replaceWhiteboardEmbed}
				setMauiEmbedFinalizeStatus={props.setMauiEmbedFinalizeStatus}
				entryPoint={props.entryPoint}
				smartCreationConfig={props.smartCreationConfig}
				smartCreationContext={props.smartCreationContext}
				productConfig={props.productConfig}
				setThinkingComplete={props.setThinkingComplete}
				finishRemixThinking={props.finishRemixThinking}
				onRemixThinkingCompleteAnimationFinished={props.onRemixThinkingCompleteAnimationFinished}
				showLocationPicker={props.showLocationPicker}
				setShowLocationPicker={props.setShowLocationPicker}
				addHistoryEntry={props.addHistoryEntry}
				addHistoryStackEntry={props.addHistoryStackEntry}
				onVariantUpdateComplete={props.onVariantUpdateComplete}
				onSubtypeMismatch={props.onSubtypeMismatch}
				onRemixInsertSuccess={props.productIntegration.onRemixInsertSuccess}
				onFinalizeSuccess={props.productIntegration.onFinalizeSuccess}
				onFinalizeFailed={props.productIntegration.onFinalizeFailed}
				scheduleRemixRetentionCheck={props.scheduleRemixRetentionCheck}
				isUpdate={props.isUpdate}
				historyEntryForUpdate={props.historyEntryForUpdate}
				dynamicUiSubtypeForUpdate={props.dynamicUiSubtypeForUpdate}
			>
				{/*
				 * Mounted for the whole flow so insertion and finalization survive the window where the
				 * machine reports `!isThinking`. It only consumes `finalMessage`; the subtree below
				 * produces it, which is why that one stays mounted too and renders `null` instead.
				 */}
				{fg('platform_editor_ai_remix_in_editor_loading') ? <OnCompleteSync /> : null}
				<RemixContentComponentInner
					isUiVisible={isRemixUiVisible}
					clearThinkingDecoration={props.clearThinkingDecoration}
					replaceWhiteboardEmbed={props.replaceWhiteboardEmbed}
					handleModalClose={props.handleModalClose}
					isModalOpen={props.isModalOpen}
					isComplete={props.isComplete}
					selectedContext={props.selectedContext}
					editorApi={props.editorApi}
					setThinkingComplete={props.setThinkingComplete}
					finishRemixThinking={props.finishRemixThinking}
					onStartThinking={props.onStartThinking}
					onDismissThinkingUi={props.onDismissThinkingUi}
					onStopThinking={props.onStopThinking}
					onRegisterAbortRequest={props.onRegisterAbortRequest}
					onRegisterEditorRemixUxHandles={props.onRegisterEditorRemixUxHandles}
					onRegisterEditorRemixMutationCancellationSink={
						props.onRegisterEditorRemixMutationCancellationSink
					}
					remixModalMetadata={props.remixModalMetadata}
					remixSource={props.remixSource}
					isThinking={props.isThinking}
					isEditorRemixStateRearchitectureTreatment={
						props.isEditorRemixStateRearchitectureTreatment
					}
					productIntegration={props.productIntegration}
					productConfig={props.productConfig}
					hiddenOptions={props.hiddenOptions}
				/>
			</EditorRemixContextProvider>
		</FlagsProvider>
	) : (
		<RemixContentComponentInner
			isUiVisible={isRemixUiVisible}
			clearThinkingDecoration={props.clearThinkingDecoration}
			replaceWhiteboardEmbed={props.replaceWhiteboardEmbed}
			handleModalClose={props.handleModalClose}
			isModalOpen={props.isModalOpen}
			isComplete={props.isComplete}
			selectedContext={props.selectedContext}
			editorApi={props.editorApi}
			setThinkingComplete={props.setThinkingComplete}
			finishRemixThinking={props.finishRemixThinking}
			onStartThinking={props.onStartThinking}
			onDismissThinkingUi={props.onDismissThinkingUi}
			onStopThinking={props.onStopThinking}
			onRegisterAbortRequest={props.onRegisterAbortRequest}
			onRegisterEditorRemixUxHandles={props.onRegisterEditorRemixUxHandles}
			onRegisterEditorRemixMutationCancellationSink={
				props.onRegisterEditorRemixMutationCancellationSink
			}
			remixModalMetadata={props.remixModalMetadata}
			remixSource={props.remixSource}
			isThinking={props.isThinking}
			isEditorRemixStateRearchitectureTreatment={props.isEditorRemixStateRearchitectureTreatment}
			productIntegration={props.productIntegration}
			productConfig={props.productConfig}
			hiddenOptions={props.hiddenOptions}
		/>
	);

	if (props.remixModalMetadata?.aiFeatureName) {
		return (
			<AIMateAnalyticsContext
				data={{ attributes: { aiFeatureName: props.remixModalMetadata.aiFeatureName } }}
			>
				{inner}
			</AIMateAnalyticsContext>
		);
	}

	return inner;
};
